import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import { FetchApiCallback } from '../../../common/api/Api.hooks';
import FormikGrid from '../../../common/formik/FormikGrid';
import FormikButton from '../../../common/formik/FormikButton';
import FormikTextField from '../../../common/formik/FormikTextField';
import { PageType } from '../ProductPage';
import { getProductEntityName } from '../Product.config';
import {
  useCreateProductDesignInput,
  useCreateProductDesignOutput,
  useCreateProductDesignSpec,
  useCreateProductUserNeed,
  useCreateProductValidation,
  useCreateProductVerification,
  useCreateProductHazard
} from '../Product.hooks';
import { ProductEntity } from '../Product.interface';

const pages = [
  'userNeed',
  'designInput',
  'designOutput',
  'verification',
  'validation',
  'designSpec',
  'hazard'
] as const;

export type ProductPageType = typeof pages[number];

function shouldShowCreateButton(page: PageType): page is ProductPageType {
  return pages.includes(page as ProductPageType);
}

export interface BaseProductEntityCreateProps {
  productId: number;
  onCreate?: (entity: ProductEntity) => void;
}

export interface ProductEntityCreateButtonProps
  extends BaseProductEntityCreateProps {
  page: PageType;
}

const ProductEntityCreateButton: React.FC<ProductEntityCreateButtonProps> = (
  props: ProductEntityCreateButtonProps
) => {
  const { page } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [didCreate, setDidCreate] = useState<boolean>(false);

  if (!shouldShowCreateButton(page)) {
    return null;
  }
  const displayName: string = getProductEntityName(page);
  return (
    <>
      <div>
        <Button
          color='primary'
          variant='contained'
          onClick={() => setOpen(true)}
        >
          Add {displayName}
        </Button>
      </div>
      {open && (
        <ProductEntityCreateDialog
          {...props}
          page={page}
          onClose={() => setOpen(false)}
          onCreate={(entity: ProductEntity) => {
            setOpen(false);
            setDidCreate(true);
            props.onCreate?.(entity);
          }}
        />
      )}

      <AppSnackbar
        severity='success'
        open={didCreate}
        onClose={() => setDidCreate(false)}
      >
        {displayName} created successfully.
      </AppSnackbar>
    </>
  );
};

export default ProductEntityCreateButton;

export interface ProductEntityCreateDialogProps
  extends BaseProductEntityCreateProps {
  page: ProductPageType;
  onClose: () => void;
}

const ProductEntityCreateDialog: React.FC<ProductEntityCreateDialogProps> = (
  props: ProductEntityCreateDialogProps
) => {
  const { page, productId, onClose, onCreate } = props;
  const userNeed = useCreateProductUserNeed(productId);
  const designInput = useCreateProductDesignInput(productId);
  const designOutput = useCreateProductDesignOutput(productId);
  const verification = useCreateProductVerification(productId);
  const validation = useCreateProductValidation(productId);
  const designSpec = useCreateProductDesignSpec(productId);
  const hazard = useCreateProductHazard(productId);

  const displayName: string = getProductEntityName(page);

  const isLoading: boolean =
    userNeed.isCreatingProductUserNeed ||
    designInput.isCreatingProductDesignInput ||
    designOutput.isCreatingProductDesignOutput ||
    verification.isCreatingProductVerification ||
    validation.isCreatingProductValidation ||
    designSpec.isCreatingProductDesignSpec ||
    hazard.isCreatingProductHazard;

  const error: FetchError | undefined =
    userNeed.createProductUserNeedError ||
    designInput.createProductDesignInputError ||
    designOutput.createProductDesignOutputError ||
    verification.createProductVerificationError ||
    validation.createProductValidationError ||
    designSpec.createProductDesignSpecError ||
    hazard.createProductHazardError;

  const createEntity: FetchApiCallback = useMemo(() => {
    switch (page) {
      case 'userNeed':
        return userNeed.createProductUserNeed;
      case 'designInput':
        return designInput.createProductDesignInput;
      case 'designOutput':
        return designOutput.createProductDesignOutput;
      case 'verification':
        return verification.createProductVerification;
      case 'validation':
        return validation.createProductValidation;
      case 'designSpec':
        return designSpec.createProductDesignSpec;
      case 'hazard':
        return hazard.createProductHazard;
    }
  }, [
    page,
    userNeed.createProductUserNeed,
    designInput.createProductDesignInput,
    designOutput.createProductDesignOutput,
    verification.createProductVerification,
    validation.createProductValidation,
    designSpec.createProductDesignSpec,
    hazard.createProductHazard
  ]);

  const entity: ProductEntity | undefined = useMemo(() => {
    switch (page) {
      case 'userNeed':
        return userNeed.createdProductUserNeed;
      case 'designInput':
        return designInput.createdProductDesignInput;
      case 'designOutput':
        return designOutput.createdProductDesignOutput;
      case 'verification':
        return verification.createdProductVerification;
      case 'validation':
        return validation.createdProductValidation;
      case 'designSpec':
        return designSpec.createdProductDesignSpec;
      case 'hazard':
        return hazard.createdProductHazard;
    }
  }, [
    page,
    userNeed.createdProductUserNeed,
    designInput.createdProductDesignInput,
    designOutput.createdProductDesignOutput,
    verification.createdProductVerification,
    validation.createdProductValidation,
    designSpec.createdProductDesignSpec,
    hazard.createdProductHazard
  ]);

  useEffect(() => {
    if (entity) {
      onCreate?.(entity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  return (
    <Dialog maxWidth='xs' open onClose={onClose}>
      <Formik
        initialValues={{ title: '' }}
        onSubmit={(dto: { title: string }) => createEntity(dto)}
        validationSchema={Yup.object({
          title: Yup.string().required('Title is required.')
        })}
      >
        <Form autoComplete='off'>
          <DialogTitle>Create {displayName}</DialogTitle>
          <DialogContent>
            <FormikGrid>
              <FormikTextField
                required
                autoFocus
                name='title'
                label='Enter title'
                disabled={isLoading}
                placeholder={`Enter title of ${displayName}`}
              />
            </FormikGrid>
            {error && <Alert severity='error'>{error.message}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <FormikButton>Create</FormikButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};
